import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuCheck, LuX } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../industry.css';

const PAIN = [
  'Twilio at $0.0079+/SMS — a 5,000-person blast costs $40 before you start',
  'A2P 10DLC: 2–6 weeks of registration, brand vetting, and campaign approvals',
  'Aggregator long codes filtered as spam — open rates tanking your unsubscribe rate',
  'Per-segment billing — emoji and links silently push you into 2-segment territory',
];

const GAIN = [
  '$0.001/SMS — same 5,000-person blast costs $5 (yes, five dollars)',
  'No registration. P2P traffic through your own SIM. Send today',
  'Real consumer number = inbox delivery, not the spam folder',
  'No segment math — pay per message, not per 160 characters',
];

const DAY = [
  { time: 'Mon 9 AM', title: 'Upload Black Friday list', body: 'Drag in a 4,200-row CSV from your CRM. Auto-detect phone column, auto-dedupe, auto-strip opt-outs.' },
  { time: 'Mon 9:04 AM', title: 'Write the template', body: '"Hey {{first_name}} — flash sale, 30% off everything, code FRIDAY30. Link: {{short_link}}." Preview a sample to confirm formatting.' },
  { time: 'Mon 9:06 AM', title: 'Hit send', body: 'TextLink paces 4,200 messages across your SIMs over 18 minutes — every one delivered through a real number, every one opt-out compliant.' },
  { time: 'Mon 9:07 AM', title: 'Replies start flowing', body: 'Inbound replies hit the browser inbox — "DOES THIS WORK FOR XL?" — your team answers conversations live.' },
  { time: 'Mon 11:30 AM', title: 'Live ROAS dashboard', body: 'Delivery, click, and reply rates streaming in real-time. STOP replies auto-removed across the account.' },
];

const FAQ = [
  {
    q: 'Is sending marketing SMS without 10DLC actually compliant?',
    a: 'TextLink uses P2P (peer-to-peer) traffic through real consumer SIMs, which falls outside the A2P 10DLC framework. You\'re still required to honor opt-outs (we handle that automatically) and follow TCPA rules around consent — same as any sender. The difference is you skip the carrier registration paperwork.',
  },
  {
    q: 'Will my numbers get blocked at higher volumes?',
    a: 'Out of the box, you can comfortably send 200–400 SMS per SIM per day. Add SIMs to scale — most marketing accounts run 5–20 SIMs split across campaigns. We auto-pace sends and warm up new numbers gradually.',
  },
  {
    q: 'How do I handle STOP / unsubscribe replies?',
    a: 'Automatic. Any reply containing STOP, UNSUBSCRIBE, CANCEL, END, QUIT, etc. is added to a global opt-out list across your account — that number won\'t receive marketing SMS again, even from a different campaign.',
  },
  {
    q: 'Can I send MMS — images, GIFs, product photos?',
    a: 'Yes. Same SIM, same workflow — pass an image URL in the bulk send and it goes out as MMS. Costs are slightly higher per message depending on your SIM\'s plan.',
  },
  {
    q: 'Does this work with Klaviyo, ActiveCampaign, or my marketing automation?',
    a: 'Yes — through the SMS API or Zapier/Make connectors. Trigger SMS from any abandoned-cart, win-back, or post-purchase flow you already have running for email.',
  },
];

export default function Marketing() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="SMS Marketing Software — Bulk SMS via Your Own SIM"
        description="Run SMS marketing campaigns at $0.001 per message through your own SIM. No 10DLC paperwork, no aggregator markup, real two-way conversations. Built-in opt-out handling, MMS, and CRM integrations."
        canonical="/sms-marketing"
      />
      <HeaderOne />

      <main className="iv">
        <div className="iv-shell">

          {/* HERO */}
          <header className="iv-hero">
            <div className="iv-hero-left">
              <span className="iv-eyebrow">SMS marketing</span>
              <h1 className="iv-h1">
                Bulk SMS marketing.<br />
                <span className="iv-h1-accent">Without the 10DLC tax.</span>
              </h1>
              <p className="iv-lead">
                Run SMS campaigns at $0.001/message through your own SIM. 95% open rate, 8x the CTR of email — minus the 6-week carrier registration and the per-segment markup.
              </p>
              <div className="iv-cta-row">
                <button className="iv-btn iv-btn--primary" onClick={() => navigate('/signup')}>
                  Try free <LuArrowRight size={15} strokeWidth={2} />
                </button>
                <Link className="iv-btn iv-btn--ghost" to="/bulksms">See the bulk sender</Link>
              </div>
            </div>

            <div className="iv-hero-stats">
              <div className="iv-stat-tile iv-stat-tile--accent">
                <div className="iv-stat-num">$0.001</div>
                <div className="iv-stat-label">per message — vs $0.0079+ on Twilio</div>
              </div>
              <div className="iv-stat-tile">
                <div className="iv-stat-num">95%</div>
                <div className="iv-stat-label">SMS open rate within 3 minutes</div>
              </div>
              <div className="iv-stat-tile">
                <div className="iv-stat-num">8×</div>
                <div className="iv-stat-label">higher CTR than email marketing</div>
              </div>
              <div className="iv-stat-tile iv-stat-tile--accent">
                <div className="iv-stat-num">0 days</div>
                <div className="iv-stat-label">10DLC paperwork. None. Send today.</div>
              </div>
            </div>
          </header>

          {/* PAIN / GAIN */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">SMS marketing is great. Aggregator pricing isn't.</h2>
              <p className="iv-sub">If you've priced out a Klaviyo or Twilio SMS plan, you already know.</p>
            </div>

            <div className="iv-contrast">
              <div className="iv-contrast-panel iv-contrast-panel--bad">
                <span className="iv-contrast-eyebrow">A2P aggregators</span>
                <h3 className="iv-contrast-h3">What you're paying for today</h3>
                <ul className="iv-contrast-list">
                  {PAIN.map((p, i) => (
                    <li key={i}>
                      <span className="iv-contrast-icon"><LuX size={12} strokeWidth={2.5} /></span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="iv-contrast-panel iv-contrast-panel--good">
                <span className="iv-contrast-eyebrow">TextLink</span>
                <h3 className="iv-contrast-h3">What changes with your own SIM</h3>
                <ul className="iv-contrast-list">
                  {GAIN.map((g, i) => (
                    <li key={i}>
                      <span className="iv-contrast-icon"><LuCheck size={12} strokeWidth={2.5} /></span>
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* DAY IN THE LIFE */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">A campaign, end to end</h2>
              <p className="iv-sub">From "let's run a Black Friday blast" to "ROAS dashboard live" in under 30 minutes.</p>
            </div>

            <ul className="iv-day">
              {DAY.map((d, i) => (
                <li key={i} className="iv-day-row">
                  <div className="iv-day-time">{d.time}</div>
                  <div className="iv-day-body">
                    <h3 className="iv-day-title">{d.title}</h3>
                    <p className="iv-day-text">{d.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* NUMBERS */}
          <section className="iv-section">
            <div className="iv-numbers">
              <div className="iv-num">
                <div className="iv-num-big"><span>87%</span></div>
                <div className="iv-num-label">cheaper than Twilio at marketing volume</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">3 min</div>
                <div className="iv-num-label">average time-to-open on SMS marketing</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">$5</div>
                <div className="iv-num-label">to send 5,000 SMS through your own SIM</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">∞</div>
                <div className="iv-num-label">campaigns per month — no platform fee</div>
              </div>
            </div>
          </section>

          {/* USE CASES — alternating */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">The campaigns that actually move revenue</h2>
              <p className="iv-sub">Five SMS workflows that print money — set up in an afternoon.</p>
            </div>

            <div className="iv-cases">

              <div className="iv-case">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Promo blasts</span>
                  <h3 className="iv-case-h3">Flash sales that hit the inbox in 90 seconds.</h3>
                  <p className="iv-case-p">
                    Promo codes, restock alerts, doorbusters. SMS gets opened in three minutes — perfect for time-bound campaigns where email gets buried.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> Personalized via {`{{first_name}}`}, {`{{last_purchase}}`}, anything from your CSV</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> MMS for product photos and looping GIFs</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Built-in short link tracking (optional)</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — Black Friday</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Hey Sara — flash sale just dropped. 30% off everything, code FRIDAY30. Ends midnight: tlsms.co/bf
                    </div>
                    <div className="iv-msg-meta iv-msg-meta--right">4,213 / 4,213 delivered</div>
                  </div>
                </div>
              </div>

              <div className="iv-case iv-case--reverse">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Abandoned cart recovery</span>
                  <h3 className="iv-case-h3">Win back the cart before they forget.</h3>
                  <p className="iv-case-p">
                    Trigger an SMS 30 minutes after cart abandonment from Klaviyo, Shopify Flow, or your custom backend. Recovery rates 3–5× better than email reminders alone.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> REST API trigger from any e-com platform</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Two-way: customer replies, you answer</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> A/B test message variants by segment</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — 30 min after cart abandon</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Hey John — you left the Patagonia jacket in your cart. Want me to hold it? Reply Y and I'll lock the price for 24h.
                    </div>
                    <div className="iv-msg-bubble">Y</div>
                    <div className="iv-msg-meta">John — replied 4 min later</div>
                  </div>
                </div>
              </div>

              <div className="iv-case">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Reactivation</span>
                  <h3 className="iv-case-h3">Win back lapsed customers without burning email reputation.</h3>
                  <p className="iv-case-p">
                    Customers who haven't bought in 90 days don't open emails. They open SMS. Send a personalized reactivation offer based on their last purchase category — typical lift is 3–7% reactivation.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> Segment by purchase history, lifetime value, or any CSV column</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Auto opt-out compliance — STOP replies handled across the account</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Replies thread back to your team's inbox in real time</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — 90-day lapse</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Hey Maria, it's been a while. We restocked the green dress you almost bought in March — 20% off if you grab it this week. tlsms.co/m23
                    </div>
                    <div className="iv-msg-meta iv-msg-meta--right">7.2% reactivation rate</div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* FAQ */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">SMS marketing — common questions</h2>
              <p className="iv-sub">Compliance, scaling, and how this fits with the rest of your stack.</p>
            </div>

            <div className="iv-faq">
              {FAQ.map((f, i) => (
                <div key={i} className="iv-faq-item">
                  <h3 className="iv-faq-q">{f.q}</h3>
                  <p className="iv-faq-a">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="iv-section">
            <div className="iv-final">
              <h2>Send your first 5,000 SMS for $5.</h2>
              <p>Sign up free, pair an Android, run your first campaign by lunch.</p>
              <div className="iv-cta-row">
                <button className="iv-btn iv-btn--white" onClick={() => navigate('/signup')}>
                  Try free <LuArrowRight size={15} strokeWidth={2} />
                </button>
                <Link className="iv-btn iv-btn--white-ghost" to="/pricing">See pricing</Link>
              </div>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
